import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { combineReducers } from 'redux';
import { ordersApi } from './apis/orders';
import { eventsApi } from './apis/events';
import { ticketsApi } from './apis/tickets';
import { businessesApi } from './apis/business';
import { eventsReducer } from './slices/events';
import { usersReducer } from './slices/users';
import { usersApi } from './apis/users';
import { businessReducer } from './slices/business';
import { ordersReducer } from './slices/orders';
import { ticketsReducer } from './slices/tickets';
import { couponsApi } from './apis/coupons';
import { communicationsApi } from './apis/communications';
import { authReducer } from './slices/auth';
import { referralsApi } from './apis/referrals';
import { commonApi } from './apis/common';
import { paymentsApi } from './apis/payment';
import { bankAccountApi } from './apis/bank-account';
import { wireApi } from './apis/wire';
import { aiQueryApi } from './apis/ai-query';
import { customerApi } from './apis/customer';
import { listApi } from './apis/lists';
import { scannerKeysApi } from './apis/scanner-keys';
import { scannerApi, scannerPanelApi } from './apis/scanner';
import { binsApi } from './apis/bins';
import { cardPromotionsApi } from './apis/card-promotions';
import { checkoutEventsApi } from './apis/checkout-events';
import { checkoutSessionApi } from './apis/checkout-session';
import { staffApi } from './apis/staff';
import { venuesApi } from './apis/venues';
import { eventSectorsApi } from './apis/event-sectors';
import { platformConfigApi } from './apis/platform-config';
import { boardsApi } from './apis/boards';
import { privateEventsApi } from './apis/private-events';
import { chargesApi } from './apis/charges';
import { billingApi } from './apis/billing';
import { emailApi } from './apis/email';
import { commissionApi } from './apis/commission';
import { crmApi } from './apis/crm';
import { crmLeadsApi } from './apis/crm-leads';
import { eventMapApi } from './apis/event-map';

export const store = configureStore({
  reducer: {
    [ordersApi.reducerPath]: ordersApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [ticketsApi.reducerPath]: ticketsApi.reducer,
    [businessesApi.reducerPath]: businessesApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [couponsApi.reducerPath]: couponsApi.reducer,
    [communicationsApi.reducerPath]: communicationsApi.reducer,
    [referralsApi.reducerPath]: referralsApi.reducer,
    [commonApi.reducerPath]: commonApi.reducer,
    [paymentsApi.reducerPath]: paymentsApi.reducer,
    [bankAccountApi.reducerPath]: bankAccountApi.reducer,
    [wireApi.reducerPath]: wireApi.reducer,
    [aiQueryApi.reducerPath]: aiQueryApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [listApi.reducerPath]: listApi.reducer,
    [scannerKeysApi.reducerPath]: scannerKeysApi.reducer,
    [scannerApi.reducerPath]: scannerApi.reducer,
    [scannerPanelApi.reducerPath]: scannerPanelApi.reducer,
    [binsApi.reducerPath]: binsApi.reducer,
    [cardPromotionsApi.reducerPath]: cardPromotionsApi.reducer,
    [checkoutEventsApi.reducerPath]: checkoutEventsApi.reducer,
    [checkoutSessionApi.reducerPath]: checkoutSessionApi.reducer,
    [staffApi.reducerPath]: staffApi.reducer,
    [venuesApi.reducerPath]: venuesApi.reducer,
    [eventSectorsApi.reducerPath]: eventSectorsApi.reducer,
    [platformConfigApi.reducerPath]: platformConfigApi.reducer,
    [boardsApi.reducerPath]: boardsApi.reducer,
    [privateEventsApi.reducerPath]: privateEventsApi.reducer,
    [chargesApi.reducerPath]: chargesApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [emailApi.reducerPath]: emailApi.reducer,
    [commissionApi.reducerPath]: commissionApi.reducer,
    [crmApi.reducerPath]: crmApi.reducer,
    [crmLeadsApi.reducerPath]: crmLeadsApi.reducer,
    [eventMapApi.reducerPath]: eventMapApi.reducer,
    events: eventsReducer,
    users: usersReducer,
    businesses: businessReducer,
    orders: ordersReducer,
    tickets: ticketsReducer,
    auth: authReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware()
    .concat(ordersApi.middleware)
    .concat(eventsApi.middleware)
    .concat(ticketsApi.middleware)
    .concat(businessesApi.middleware)
    .concat(usersApi.middleware)
    .concat(couponsApi.middleware)
    .concat(communicationsApi.middleware)
    .concat(referralsApi.middleware)
    .concat(commonApi.middleware)
    .concat(paymentsApi.middleware)
    .concat(bankAccountApi.middleware)
    .concat(wireApi.middleware)
    .concat(aiQueryApi.middleware)
    .concat(customerApi.middleware)
    .concat(listApi.middleware)
    .concat(scannerKeysApi.middleware)
    .concat(scannerApi.middleware)
    .concat(scannerPanelApi.middleware)
    .concat(binsApi.middleware)
    .concat(cardPromotionsApi.middleware)
    .concat(checkoutEventsApi.middleware)
    .concat(checkoutSessionApi.middleware)
    .concat(staffApi.middleware)
    .concat(venuesApi.middleware)
    .concat(eventSectorsApi.middleware)
    .concat(platformConfigApi.middleware)
    .concat(boardsApi.middleware)
    .concat(privateEventsApi.middleware)
    .concat(chargesApi.middleware)
    .concat(billingApi.middleware)
    .concat(emailApi.middleware)
    .concat(commissionApi.middleware)
    .concat(crmApi.middleware)
    .concat(crmLeadsApi.middleware)
    .concat(eventMapApi.middleware),
});

const rootReducer = combineReducers({
  events: eventsReducer,
  users: usersReducer,
  businesses: businessReducer,
  orders: ordersReducer,
  tickets: ticketsReducer,
  auth: authReducer,
});

setupListeners(store.dispatch);

export {
  useCreateOrderMutation,
  useGetEventOrdersQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useGetEventOrdersExportQuery,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useUpdateOrderStatusMutation,
  useDecodeQrCodeQuery,
  useUpdateUsedOrderItemMutation,
  useSendOrderEmailMutation,
  useChangeOrderEmailMutation,
  useRegenerateOrderQrsMutation,
  useGetOrderByCustomIdQuery,
  useLazyGetPublicOrderByDisplayIdQuery,
  useRequestPublicRefundMutation,
  useUpdateOrderPublicEmailMutation,
  useGetSellerOrdersQuery,
  useGetSellerSummaryQuery,
  useGetOneSellerSummaryQuery,
  useGetOrderTotalsQuery,
  useGetEventOrdersReportQuery,
  useGetQRImageQuery,
  useLazyGetQRImageQuery,
  useGetOrderByIdQuery,
  useLazyGetOrderByIdQuery,
  useValidateTicketExclusiveMutation,
  useGetSellerSummaryXlsxMutation,
  useGetOrderReferralDataQuery,
  useGetEventSalesByUserQuery,
  useGetQuickSellerSummaryQuery,
  useGetEventOrderItemsQuery,
  useCreateBatchManualOrdersMutation,
  useLinkPrintedTicketMutation,
  useUnlinkPrintedTicketMutation,
} from './apis/orders';

export {
  useGetAllBusinessEventsQuery,
  useGetEventByIdQuery,
  useLazyGetEventByIdQuery,
  useGetBusinessEventsQuery,
  useGetCurrentAgentQuery,
  useGetAllEventsQuery,
  useCreateEventMutation,
  useDeleteEventMutation,
  useUpdateEventMutation,
  useGetEventUsersQuery,
  useAddUserToEventMutation,
  useDeleteUserEventMutation,
  useAddEventTransactionMutation,
  useDeleteEventTransactionMutation,
  useUpdateEventTransactionMutation,
  useGetEventTransactionsQuery,
  useGetEventCapacityQuery,
  useCountSellerQrCodeMutation,
  useGetSellerEventQrQuery,
  useLazyGetSellerEventQrQuery,
  useUpdateUserEventMutation,
  useGetEventBalanceQuery,
  useCreateWireTransferMutation,
  useImportSellersToEventMutation,
  useReserveSeatMutation,
  useFreeSeatMutation,
  useGetEventAssistanceReportQuery,
  useGetEventSellersReportQuery,
  useGetEventsByQueryQuery,
  useGetAllEventsBasicQuery,
  useGetBusinessEventsBasicQuery,
  useCreateAudienceDiscountMutation,
  useCloneEventMutation,
  useGetEventBySlugQuery,
  useGetAudienceDiscountsQuery,
  useUpdateAudienceDiscountMutation,
  useDeleteAudienceDiscountMutation,
  useCreateShowtimeMutation,
  useUpdateShowtimeMutation,
  useDeleteShowtimeMutation,
  useGetShowtimesQuery,
  useGetEventSubscriptionQuery,
  useCreateEventSubscriptionMutation,
  useDeleteEventBySuscribedIdMutation,
  useGetCategoriesByEventIdQuery,
  useGetCategoriesQuery,
  useCreateEventCategoryMutation,
  useDeleteEventCategoryMutation,
  useGetPublicProvincesQuery,
  useGetPublicCitiesByProvinceQuery,
  useGetPublicCategoriesQuery,
  useGetPublicProducersQuery,
  useGetPublicCategoryEventsQuery,
  useGetEventResponsesTableQuery,
  useGetEventItemsExportQuery,
  useGetEventQuestionsQuery,
  useGetBusinessEventPollQuestionsQuery,
  useCreatePollQuestionMutation,
  useUpdatePollQuestionMutation,
  useDeletePollQuestionMutation,
  useGetBusinessEventAttendeeFieldsQuery,
  useCreateAttendeeFieldMutation,
  useUpdateAttendeeFieldMutation,
  useDeleteAttendeeFieldMutation,
  useGetEventResponsesByOrderIdMutation,
  useGetEventsSummaryQuery,
  useGetAllEventsPendingBalanceQuery,
  useGetPendingBalanceAtQuery,
  useGetEventSummaryPDFMutation,
  useGetEventsSummaryXlsxMutation,
  useGetPlatformSalesChartQuery,
  useGetPlatformChartsQuery,
  useGetPlatformRangeChartsQuery,
  useGetPlatformMonthlyChartsQuery,
  useGetTopGrossingQuery,
  useGetEventStatsQuery,
  useGetEventSeatsRawQuery,
  useBulkCreateEventSeatsFromIdsMutation,
  useUpdateEventSeatMutation,
  useDeleteEventSeatMutation,
  useGetSeatsAvailabilityQuery,
  useLazyGetBestSeatsQuery,
  useBlockEventSeatsMutation,
} from './apis/events';

export {
  useGetVenuesQuery,
  useGetVenueByIdQuery,
  useGetVenueSeatsQuery,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useDeleteVenueMutation,
  useBulkCreateVenueSeatsMutation,
  useGetVenueLayoutQuery,
  useGetVenueShapesQuery,
  useSaveVenueLayoutMutation,
  usePublishVenueMutation,
  useDuplicateVenueMutation,
} from './apis/venues';

export {
  useGetEventSectorsQuery,
  useCreateEventSectorMutation,
  useUpdateEventSectorMutation,
  useDeleteEventSectorMutation,
} from './apis/event-sectors';

export {
  useGetCouponByIdQuery,
  useGetAllCouponsQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
  useCheckCouponValidationMutation,
} from './apis/coupons';

export {
  useGetCommunicationsQuery,
  useUpdateReminderConfigMutation,
  useSendManualReminderMutation,
  useGetReminderPreviewQuery,
} from './apis/communications';

export {
  useGetReferralByIdQuery,
  useGetAllReferralsQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useDeleteReferralMutation,
  useGetReferralNameByCodeQuery,
} from './apis/referrals';

export {
  useGetMPAuthLinkQuery,
  useUploadImageMutation,
} from './apis/common';

export {
  useGetAllTicketsQuery,
  useGetTicketByIdQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useDeleteTicketMutation,
  useReserveTicketsMutation,
  useReleaseTicketReservationMutation,
  useCreateReservationMutation,
  useGetReservationAndSeatsQuery,
} from './apis/tickets';

export {
  useGetAllBusinessesQuery,
  useGetBusinessByIdQuery,
  useCreateBusinessMutation,
  useDeleteBusinessMutation,
  useUpdateBusinessMutation,
  useGetBusinessUsersQuery,
  useGetBusinessInvitationsQuery,
  useCreateInvitationMutation,
  useGetEmailInvitationsQuery,
  useAcceptInvitationMutation,
  useRejectInvitationMutation,
  useGetBusinessBySubdomainQuery,
  useGetBusinessByDomainQuery,
  useLazyGetBusinessByDomainQuery,
  useGetBusinessBySlugQuery,
  useLazyGetBusinessBySlugQuery,
  useDeleteInvitationMutation,
  useRemoveUserFromBusinessMutation,
  useAuthorizeMercadoPagoMutation,
  useGetPaymentConfigQuery,
  useUpdateUserBusinessMutation,
  useDeletePaymentConfigMutation,
  useGetBusinessCashRegistersQuery,
  useCreateBusinessCashRegisterMutation,
  useUpdateBusinessCashRegisterMutation,
  useDeleteBusinessCashRegisterMutation,
  useGetBusinessCashRegisterByIdQuery,
  useGetOpenCashRegisterSessionsQuery,
  useGetCashRegisterSessionsQuery,
  useGetCashRegisterSessionQuery,
  useGetCashRegisterSessionReportQuery,
  useOpenCashRegisterSessionMutation,
  useCloseCashRegisterSessionMutation,
  useCreateCashMovementMutation,
  useGetCashPaymentMethodsQuery,
  useCreateCashPaymentMethodMutation,
  useUpdateCashPaymentMethodMutation,
  useDeleteCashPaymentMethodMutation,
  useGetBusinessMembersQuery,
  useCreateBusinessMemberMutation,
  useUpdateBusinessMemberMutation,
  useUpdateBusinessMemberStatusMutation,
  useExportMembersXlsxMutation,
  useLazyGetBusinessBySubdomainQuery,
  useCheckMemberStatusMutation,
  useGetSellerGroupsQuery,
  useCreateSellerGroupMutation,
  useUpdateSellerGroupMutation,
  useDeleteSellerGroupMutation,
  useUpdatePaymentConfigMutation,
  useDestroyBusinessMemberMutation,
} from './apis/business';

export {
  useLogInMutation,
  useGetUserByFirebaseIdQuery,
  useCreateUserMutation,
  useGetUserByConfirmationCodeQuery,
  useVerifyUserMutation,
  useResendVerificationEmailMutation,
  useGetUserBusinessesQuery,
  useValidateGoogleLoginMutation,
  useCheckEmailExistsMutation,
  useLazyGetUserByFirebaseIdQuery,
  useGetAllUserEventsQuery,
  useGetUserQuery,
  useSendRecoverPasswordEmailMutation,
  useResetPasswordMutation,
} from './apis/users';

export {
  useGetPaymentByIdQuery,
  useSyncPaywayLinkPaymentQuery,
  useGetPaymentByOrderIdQuery,
  useGetAvanttiPaymentReportQuery,
  useGetEventPaymentReportQuery,
  useGetEventRevenueReportQuery,
  useGetPlatformRevenueReportQuery,
  useGetSuspiciousCardOrdersQuery,
  useGetEventPaymentReportXlsxMutation,
} from './apis/payment';

export {
  useGetBankAccountsQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
} from './apis/bank-account';

export {
  useGetWiresRequestsQuery,
  useCreateWireRequestMutation,
  useCreateApprovedWireRequestMutation,
  useUpdateWireRequestMutation,
  useDeleteWireRequestMutation,
  useCreateTransferMutation,
  useUpdateTransferMutation,
  useApproveRequestMutation,
  useDownloadWiresMutation,
  useDeleteTransferMutation,
  useGetWireTransfersReportQuery,
  useDownloadWireTransfersReportMutation,
} from './apis/wire';

export {
  useGetInvoicesReportQuery,
} from './apis/billing';

export {
  useGetEmailStatsQuery,
} from './apis/email';

export {
  useGetMyCommissionsQuery,
  useGetCommissionSummaryQuery,
  useGetCommissionCostsQuery,
  usePayCommissionBalancesMutation,
} from './apis/commission';

export {
  useGenerateAiQueryMutation,
} from './apis/ai-query';

export {
  useUpdatePlatformConfigMutation,
} from './apis/platform-config';

export {
  useGetPrivateEventsBalanceQuery,
  useGetPrivateEventsBalanceByEventQuery,
  useGetPrivateEventsLoadsQuery,
  useCreatePrivateEventsLoadMutation,
  useDeletePrivateEventsLoadMutation,
  useGetPrivateEventConfigQuery,
  useUpdatePrivateEventConfigMutation,
  useGetPublicCreditPriceQuery,
} from './apis/private-events';
export { useCreateLeadMutation, useUpdateLeadMutation } from './apis/crm-leads';

export {
  useGetBoardsQuery,
  useGetBoardByIdQuery,
  useGetBoardDetailQuery,
  useCreateBoardMutation,
  useUpdateBoardMutation,
  useDuplicateBoardMutation,
  useCreateBoardSettlementMutation,
  useDeleteBoardMutation,
} from './apis/boards';

export {
  useGetEventChargesQuery,
  useCreateEventChargeMutation,
  useUpdateEventChargeMutation,
  useDeleteEventChargeMutation,
} from './apis/charges';

export {
  useGetCustomersQuery,
  useEditCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerGroupsQuery,
  useCreateCustomerGroupMutation,
  useEditCustomerGroupMutation,
  useDeleteCustomerGroupMutation,
  useExportCustomersXlsxMutation,
} from './apis/customer';

export {
  useGetListsQuery,
  useCreateListMutation,
  useGetListByIdQuery,
  useGetListsByEventIdQuery,
  useGetListGuestsQuery,
  useCreateListGuestsMutation,
  useAcceptListGuestsMutation,
  useCreateAdminListMutation,
} from './apis/lists';

export {
  useUseScannerKeyMutation,
  useCreateScannerKeyMutation,
} from './apis/scanner-keys';

export {
  useGetBinDataQuery,
  useLazyGetBinDataQuery,
} from './apis/bins';
export type { BinData, InstallmentOption } from './apis/bins';
export {
  useGetCardPromotionsQuery,
  useCreateCardPromotionMutation,
  useUpdateCardPromotionMutation,
  useDeleteCardPromotionMutation,
  useGetEventCardPromotionsQuery,
} from './apis/card-promotions';
export { useRecordCheckoutEventsMutation } from './apis/checkout-events';
export {
  useCreateCheckoutSessionMutation,
  useGetCheckoutSessionQuery,
  useUpdateCheckoutSessionMutation,
  useRenewCheckoutSessionMutation,
  useCreateCheckoutOrderMutation,
  useCancelCheckoutSessionMutation,
  useCreatePaymentAttemptMutation,
  useGetSharedTicketQuery,
} from './apis/checkout-session';

export type { NavePaymentData } from './apis/orders';

export {
  useGetStaffRolesQuery,
  useCreateStaffRoleMutation,
  useUpdateStaffRoleMutation,
  useDeleteStaffRoleMutation,
  useReplicateStaffRolesMutation,
  useGetStaffMembersQuery,
  useCreateStaffMemberMutation,
  useUpdateStaffMemberMutation,
  useDeleteStaffMemberMutation,
  useEnableStaffMemberMutation,
  useDisableStaffMemberMutation,
  useResendStaffMemberQrMutation,
  useRegenerateStaffMemberQrMutation,
  useImportStaffMembersMutation,
  useGetStaffDoorsQuery,
  useCreateStaffDoorMutation,
  useUpdateStaffDoorMutation,
  useDeleteStaffDoorMutation,
  useDuplicateStaffDoorMutation,
  useGetStaffReportsQuery,
  useExportStaffReportsMutation,
  useGenerateStaffLinkMutation,
  useGetActiveStaffLinkQuery,
  useValidateStaffRegistrationTokenQuery,
  useSubmitStaffRegistrationMutation,
} from './apis/staff';

export {
  setCreatedOrder, setOrderItemData, setPollData, setReservation, clearReservation, selectReservation,
} from './slices/orders';
export { setBusiness } from './slices/business';
export {
  setAuthToken, setSelectedBusiness, setUserBusinesses, clearSelectedBusiness,
} from './slices/users';
export { setTickets, setTicketsSelected } from './slices/tickets';
export {
  logout, loginSuccess, loadAuth, authResolved,
} from './slices/auth';
export { useGetCrmSummaryQuery } from './apis/crm';
export {
  scannerApi,
  useOpenScannerSessionMutation,
  usePreviewScannerSessionMutation,
  useGetCurrentScannerSessionQuery,
  useGetTodayScannerEventsQuery,
  useGetScannerEventQuery,
  useGetScanSummaryQuery,
  useCreateScanMutation,
  useConfirmScanMutation,
  useOrderScanMutation,
  useLazySearchPersonQuery,
  useGetScannerAccessQuery,
  useRotateScannerAccessMutation,
  useGetScannerActivityQuery,
  useEndScannerSessionMutation,
} from './apis/scanner';
export type RootState = ReturnType<typeof rootReducer>;

export {
  useAssignVenueSeatsToSectorMutation,
  useMoveEventSeatMutation,
  useSetEventSeatStatusMutation,
  useRemoveEventSeatMutation,
  useImportEventSeatsMutation,
  eventMapApi,
  invalidateEventMapCaches,
} from './apis/event-map';
